import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { fetchDashboardData, setMetricaActiva } from '../../redux/ventasDashboardActions';
import MetricCard from './MetricCard';
import SeccionMetasVenta from './SeccionMetasVenta';
import SeccionTendencias from './SeccionTendencias';
import SeccionDetallePeriodo from './SeccionDetallePeriodo';
import SeccionComparativoDetallado from './SeccionComparativoDetallado';
import Select from 'react-select';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
    PieChart, Pie, Cell,
    BarChart, Bar
} from 'recharts';
import swal from 'sweetalert';

const VentasDashboardUI = () => {
    const dispatch = useDispatch();
    const store = useStore();

    const {
        cachePorAnio, loading, anioActivo, sucursalActiva, mesActivo, sectorActivo, metricaActiva, productosActivos, productosDisponibles, verPorActivo, semanaActiva, fechaDesde, fechaHasta
    } = useSelector(state => state.ventasDashboard);

    const [localSelectedProducts, setLocalSelectedProducts] = useState([]);
    const [sucursalesList, setSucursalesList] = useState([]);
    const [sectoresDisponibles, setSectoresDisponibles] = useState([]);
    const [isSyncing, setIsSyncing] = useState(false);

    const prodsStr = productosActivos && productosActivos.length > 0 ? productosActivos.join(',') : 'ALL';
    const cacheKey = `${anioActivo}_${sucursalActiva || 'ALL'}_${verPorActivo}_${mesActivo || 'ALL'}_${semanaActiva || 'ALL'}_${fechaDesde || 'ALL'}_${fechaHasta || 'ALL'}_${sectorActivo || 'ALL'}_${prodsStr}`;
    const dashboardData = cachePorAnio[cacheKey];

    useEffect(() => {
        fetchDashboardData('2026', '', 'mes', '', '', '', '', '', [], dispatch, store.getState);

        fetch('/api/sucursales/SucursalEmpresa')
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) setSucursalesList(data);
            })
            .catch(e => console.error(e));

        fetch('/api/dashboard-ventas/sectores-disponibles', { headers: { 'Authorization': `Bearer ${store.getState().user?.token || ''}` } })
            .then(res => res.json())
            .then(json => {
                if (json.success && Array.isArray(json.data)) {
                    setSectoresDisponibles(json.data);
                }
            })
            .catch(e => console.error(e));
    }, [dispatch, store]);

    useEffect(() => {
        if (productosDisponibles && productosActivos) {
            const mapped = productosDisponibles.filter(p => productosActivos.includes(p.value));
            setLocalSelectedProducts(mapped);
        }
    }, [productosActivos, productosDisponibles]);

    const handleSyncData = async () => {
        setIsSyncing(true);
        try {
            const res = await fetch('/api/dashboard-ventas/sync', { method: 'POST', headers: { 'Authorization': `Bearer ${store.getState().user?.token || ''}` } });
            if (res.ok) {
                await swal("¡Excelente!", "Datos de ventas sincronizados y actualizados correctamente.", "success");
                window.location.reload();
            } else {
                swal("Oops", "Hubo un error al intentar sincronizar los datos.", "error");
            }
        } catch (error) {
            console.error("Error sincronizando:", error);
            swal("Error", "Error de red al intentar sincronizar los datos.", "error");
        } finally {
            setIsSyncing(false);
        }
    };

    const handleAnioChange = (e) => { fetchDashboardData(e.target.value, sucursalActiva, verPorActivo, mesActivo, semanaActiva, fechaDesde, fechaHasta, sectorActivo, productosActivos, dispatch, store.getState); };
    const handleSucursalChange = (e) => { fetchDashboardData(anioActivo, e.target.value, verPorActivo, mesActivo, semanaActiva, fechaDesde, fechaHasta, sectorActivo, productosActivos, dispatch, store.getState); };
    const handleSectorChange = (e) => { fetchDashboardData(anioActivo, sucursalActiva, verPorActivo, mesActivo, semanaActiva, fechaDesde, fechaHasta, e.target.value, productosActivos, dispatch, store.getState); };

    const handleApplyProducts = () => {
        const productIds = localSelectedProducts ? localSelectedProducts.map(p => p.value) : [];
        fetchDashboardData(anioActivo, sucursalActiva, verPorActivo, mesActivo, semanaActiva, fechaDesde, fechaHasta, sectorActivo, productIds, dispatch, store.getState);
    };

    const handleVerPorChange = (e) => {
        fetchDashboardData(anioActivo, sucursalActiva, e.target.value, '', '', '', '', sectorActivo, productosActivos, dispatch, store.getState);
    };

    const handleMetricaChange = (e) => {
        dispatch(setMetricaActiva(e.target.value));
    };

    const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042', '#a05195', '#d45087', '#f95d6a'];

    const formatMoney = (value) => new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(value);
    const formatNumber = (value) => new Intl.NumberFormat('es-PE').format(value);
    const formatValue = (value) => metricaActiva === 'monto' ? formatMoney(value) : formatNumber(value);

    const top10Productos = useMemo(() => {
        if (!dashboardData || !dashboardData.topProductos) return [];
        const sorted = [...dashboardData.topProductos].sort((a, b) => {
            return metricaActiva === 'monto'
                ? b.ingresos_totales - a.ingresos_totales
                : b.cantidad_total - a.cantidad_total;
        });
        return sorted.slice(0, 10);
    }, [dashboardData, metricaActiva]);

    // Opciones para los filtros
    const anioOptions = [{ value: '2026', label: '2026' }, { value: '2025', label: '2025' }];
    const sucursalOptions = [{ value: '', label: 'Todas las Sucursales' }, ...sucursalesList.map(s => ({ value: s.IdSucursal.toString(), label: s.Nombre }))];
    const mesOptions = [
        { value: '', label: 'Todos' },
        { value: '1', label: 'Ene' }, { value: '2', label: 'Feb' }, { value: '3', label: 'Mar' },
        { value: '4', label: 'Abr' }, { value: '5', label: 'May' }, { value: '6', label: 'Jun' },
        { value: '7', label: 'Jul' }, { value: '8', label: 'Ago' }, { value: '9', label: 'Sep' },
        { value: '10', label: 'Oct' }, { value: '11', label: 'Nov' }, { value: '12', label: 'Dic' }
    ];

    const sectorOptions = useMemo(() => {
        const base = [{ value: '', label: 'Todos' }];
        if (sectoresDisponibles.length > 0) {
            sectoresDisponibles.forEach(sec => {
                if (sec) base.push({ value: sec, label: sec });
            });
        } else {
            base.push(
                { value: 'Panaderia', label: 'Panadería' },
                { value: 'Pasteleria', label: 'Pastelería' },
                { value: 'Bebidas', label: 'Bebidas' },
                { value: 'Campos', label: 'Campos' },
                { value: 'Otros', label: 'Otros' }
            );
        }
        return base;
    }, [sectoresDisponibles]);

    const metricaOptions = [{ value: 'monto', label: 'Monto (S/.)' }, { value: 'unidades', label: 'Unidades' }];
    const verPorOptions = [{ value: 'mes', label: 'Mes' }, { value: 'semana', label: 'Semana' }, { value: 'dia', label: 'Día' }];

    const renderFilterGroup = (label, options, value, onChange) => (
        <div className="d-flex align-items-center justify-content-start mb-3 text-left">
            <label className="text-muted font-weight-bold m-2" style={{ fontSize: '0.85rem', textTransform: 'uppercase', textAlign: 'left' }}>
                {label}
            </label>
            <div className="d-flex flex-wrap justify-content-start text-left mr-3" style={{ gap: '8px' }}>
                {options.map(opt => (
                    <button key={opt.value} className={`btn btn-sm rounded-pill px-3 ${value === opt.value ? 'btn-verde-mfp font-weight-bold shadow-sm' : 'btn-light border text-dark'}`} onClick={() => onChange({ target: { value: opt.value } })} disabled={loading}
                        style={{ fontSize: '14px' }}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    );

    const handleMesChange = (val) => {
        let selected = mesActivo ? mesActivo.split(',') : [];
        if (val === '') {
            fetchDashboardData(anioActivo, sucursalActiva, verPorActivo, '', semanaActiva, fechaDesde, fechaHasta, sectorActivo, productosActivos, dispatch, store.getState);
            return;
        }
        if (selected.includes(val)) {
            selected = selected.filter(m => m !== val);
        } else {
            selected = selected.filter(m => m !== '');
            selected.push(val);
        }
        fetchDashboardData(anioActivo, sucursalActiva, verPorActivo, selected.join(','), semanaActiva, fechaDesde, fechaHasta, sectorActivo, productosActivos, dispatch, store.getState);
    };

    const handleSemanaChange = (val) => {
        let selected = semanaActiva ? semanaActiva.split(',') : [];
        if (val === '') {
            fetchDashboardData(anioActivo, sucursalActiva, verPorActivo, mesActivo, '', fechaDesde, fechaHasta, sectorActivo, productosActivos, dispatch, store.getState);
            return;
        }
        if (selected.includes(val)) {
            selected = selected.filter(m => m !== val);
        } else {
            selected = selected.filter(m => m !== '');
            selected.push(val);
        }
        fetchDashboardData(anioActivo, sucursalActiva, verPorActivo, mesActivo, selected.join(','), fechaDesde, fechaHasta, sectorActivo, productosActivos, dispatch, store.getState);
    };

    const handleFechaChange = (tipo, val) => {
        let d = fechaDesde;
        let h = fechaHasta;
        if (tipo === 'desde') d = val;
        if (tipo === 'hasta') h = val;
        fetchDashboardData(anioActivo, sucursalActiva, verPorActivo, mesActivo, semanaActiva, d, h, sectorActivo, productosActivos, dispatch, store.getState);
    };

    const getSemanaFechas = (anio, sem) => {
        const d = new Date(anio, 0, 1);
        const dias = (sem - 1) * 7;
        d.setDate(d.getDate() + dias);
        const f1 = new Date(d);
        f1.setDate(f1.getDate() - f1.getDay() + 1);
        const f2 = new Date(f1);
        f2.setDate(f2.getDate() + 6);
        return `${f1.getDate()}/${f1.getMonth() + 1} - ${f2.getDate()}/${f2.getMonth() + 1}`;
    };

    const obtenerSemana = (fecha) => {
        const d = new Date(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()));
        const diaNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - diaNum);
        const anioInicio = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - anioInicio) / 86400000) + 1) / 7);
    };
    const semanaActual = obtenerSemana(new Date());
    const maxSemana = parseInt(anioActivo) === new Date().getFullYear() ? semanaActual : 52;
    const semanaOptions = [{ value: '', label: 'Todas' }];
    for (let i = 1; i <= maxSemana; i++) {
        semanaOptions.push({ value: i.toString(), label: `Sem. ${i} (${getSemanaFechas(parseInt(anioActivo), i)})` });
    }

    const renderDynamicSubFilter = () => {
        if (verPorActivo === 'mes') {
            const selected = mesActivo ? mesActivo.split(',') : [];
            return (
                <div className="d-flex align-items-center justify-content-start mb-3 text-left">
                    <label className="text-muted font-weight-bold m-2" style={{ fontSize: '0.85rem', textTransform: 'uppercase', textAlign: 'left' }}>
                        MESES
                    </label>
                    <div className="d-flex flex-wrap justify-content-start text-left mr-3" style={{ gap: '8px' }}>
                        {mesOptions.map(opt => {
                            const isSelected = opt.value === '' ? selected.length === 0 : selected.includes(opt.value);
                            return (
                                <button
                                    key={opt.value} className={`btn btn-sm rounded-pill px-3 ${isSelected ? 'btn-verde-mfp font-weight-bold shadow-sm' : 'btn-light border text-dark'}`}
                                    style={isSelected ? { fontSize: '14px' } : undefined}
                                    onClick={() => handleMesChange(opt.value)}
                                    disabled={loading}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        } else if (verPorActivo === 'semana') {
            const selected = semanaActiva ? semanaActiva.split(',') : [];
            return (
                <div className="d-flex align-items-center justify-content-start mb-3 text-left">
                    <div className="d-flex flex-wrap justify-content-start text-left mr-3" style={{ gap: '8px', maxHeight: '120px', overflowY: 'auto' }}>
                        {semanaOptions.map(opt => {
                            const isSelected = opt.value === '' ? selected.length === 0 : selected.includes(opt.value);
                            return (
                                <button key={opt.value} className={`btn btn-sm rounded-pill px-3 ${isSelected ? 'btn-verde-mfp font-weight-bold shadow-sm' : 'btn-light border text-dark'}`} onClick={() => handleSemanaChange(opt.value)} disabled={loading}
                                    style={isSelected ? { fontSize: '14px' } : undefined}>
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                    <span className="text-muted font-weight-bold" style={{ fontSize: '0.85rem', textTransform: 'uppercase', textAlign: 'left' }}>
                        SEMANAS
                    </span>
                </div>
            );
        } else if (verPorActivo === 'dia') {
            return (
                <div className="d-flex align-items-center justify-content-start mb-3 text-left">
                    <div className="d-flex flex-wrap align-items-center justify-content-start text-left mr-3" style={{ gap: '15px' }}>
                        <div className="d-flex align-items-center">
                            <label className="mb-0 mr-2 font-weight-bold text-secondary" style={{ fontSize: '0.85rem' }}>Desde:</label>
                            <input type="date" className="form-control form-control-sm border" value={fechaDesde || ''} onChange={(e) => handleFechaChange('desde', e.target.value)} disabled={loading} style={{ width: '150px' }} />
                        </div>
                        <div className="d-flex align-items-center">
                            <label className="mb-0 mr-2 font-weight-bold text-secondary" style={{ fontSize: '0.85rem' }}>Hasta:</label>
                            <input type="date" className="form-control form-control-sm border" value={fechaHasta || ''} onChange={(e) => handleFechaChange('hasta', e.target.value)} disabled={loading} style={{ width: '150px' }} />
                        </div>
                        <button className="btn btn-sm btn-outline-danger rounded-pill px-3" onClick={() => { handleFechaChange('desde', ''); handleFechaChange('hasta', ''); }} disabled={loading}><i className="fas fa-times mr-1"></i> Limpiar</button>
                    </div>
                    <span className="text-muted font-weight-bold" style={{ fontSize: '0.85rem', textTransform: 'uppercase', textAlign: 'left' }}>
                        FECHAS
                    </span>
                </div>
            );
        }
        return null;
    };

    const hoyDate = new Date();
    const opcionesFecha = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const fechaFormateada = hoyDate.toLocaleDateString('es-ES', opcionesFecha);

    const getInfoFiltros = () => {
        let tiempo = '';
        if (verPorActivo === 'mes') {
            const mesesStr = mesActivo ? mesOptions.filter(m => mesActivo.split(',').includes(m.value)).map(m => m.label).join(', ') : 'Todos';
            tiempo = `Meses: ${mesesStr} (${anioActivo})`;
        } else if (verPorActivo === 'semana') {
            const semStr = semanaActiva ? semanaActiva.split(',').map(s => `Sem. ${s}`).join(', ') : 'Todas';
            tiempo = `Semanas: ${semStr} (${anioActivo})`;
        } else if (verPorActivo === 'dia') {
            tiempo = `Fechas: ${fechaDesde || 'Inicio'} al ${fechaHasta || 'Fin'}`;
        }

        let suc = sucursalActiva ? sucursalesList.find(s => s.IdSucursal.toString() === sucursalActiva)?.Nombre || 'Desconocida' : 'Todas';
        let sec = sectorActivo || 'Todos';

        return `${tiempo} | Sucursal: ${suc} | Sector: ${sec}`;
    };
    const infoFiltros = getInfoFiltros();

    return (
        <div className="container-fluid px-md-5 pt-3">
            {/* Cabecera y Fecha */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                <div className="mb-3">
                    <h3 className="font-weight-bold text-dark m-0">Comparativo de ventas {parseInt(anioActivo) - 1} vs. {anioActivo}</h3>
                    <p className="text-muted mb-2">Visión general del rendimiento de ventas comparado</p>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: '15px' }}>
                        <span className="text-success font-weight-bold"><i className="fas fa-check-circle"></i> Ventas actualizadas · años: {parseInt(anioActivo) - 1}, {anioActivo}</span>
                        <button
                            className="btn btn-sm text-white rounded-pill px-3 shadow-sm"
                            style={{ backgroundColor: '#01a59c', border: 'none' }}
                            onClick={handleSyncData}
                            disabled={isSyncing}
                        >
                            {isSyncing ? <><i className="fas fa-spinner fa-spin mr-1"></i> Sincronizando...</> : <><i className="fas fa-sync-alt mr-1"></i> Sincronizar Datos</>}
                        </button>
                    </div>
                </div>
                <div
                    className="p-3 mb-3" style={{ border: '1px solid #01a59c', borderRadius: '1.25rem' }}
                >
                    <span className="text-dark font-weight-bold">
                        📅 Hoy es {fechaFormateada} — estamos en la Semana {semanaActual}.
                    </span>
                </div>
            </div>

            {/* 5 Tarjetas KPIs Principales (Debajo de cabecera) */}
            {dashboardData && dashboardData.kpis && (
                <div className="d-flex flex-wrap justify-content-between mb-4" style={{ gap: '15px' }}>
                    {/* 1. Total Vendido Año Anterior */}
                    <div className="shadow-sm flex-grow-1" style={{ minWidth: '18%', flexBasis: '18%', border: '2px solid #01a59c', borderRadius: '1.25rem' }}>
                        <div className="card-body p-3">
                            <h6 className="text-black font-bold mb-2" style={{ fontSize: '15px' }}>TOTAL VENDIDO {parseInt(anioActivo) - 1}</h6>
                            <h4 className="font-weight-bold text-dark mb-1">{formatMoney(dashboardData.kpis.montoAnterior)}</h4>
                            <label className="text-muted">{formatNumber(dashboardData.kpis.unidadesAnterior)} unidades</label>
                        </div>
                    </div>

                    {/* 2. Total Vendido Año Actual */}
                    <div className="shadow-sm flex-grow-1" style={{ minWidth: '18%', flexBasis: '18%', border: '2px solid #01a59c', borderRadius: '1.25rem' }}>
                        <div className="card-body p-3">
                            <h6 className="text-black font-bold mb-2" style={{ fontSize: '15px' }}>TOTAL VENDIDO {anioActivo}</h6>
                            <h4 className="font-weight-bold text-primary mb-1">{formatMoney(dashboardData.kpis.montoActual)}</h4>
                            <label className="text-muted">{formatNumber(dashboardData.kpis.unidadesActual)} unidades</label>
                        </div>
                    </div>

                    {/* 3. Variación */}
                    <div className="shadow-sm flex-grow-1" style={{ minWidth: '18%', flexBasis: '18%', border: '2px solid #01a59c', borderRadius: '1.25rem' }}>
                        <div className="card-body p-3">
                            <h6 className="text-black font-bold mb-2" style={{ fontSize: '15px' }}>VARIACIÓN {parseInt(anioActivo) - 1} &rarr; {anioActivo}</h6>
                            <h4 className={`font-weight-bold mb-1 ${dashboardData.kpis.crecimientoMonto >= 0 ? 'text-success' : 'text-danger'}`}>
                                {Math.abs(dashboardData.kpis.crecimientoMonto)}%
                            </h4>
                            <label className="text-muted">{dashboardData.kpis.crecimientoMonto >= 0 ? '▲ Subida' : '▼ Caída'}</label>
                        </div>
                    </div>

                    {/* 4. Unidades Vendidas */}
                    <div className="shadow-sm flex-grow-1" style={{ minWidth: '18%', flexBasis: '18%', border: '2px solid #01a59c', borderRadius: '1.25rem' }}>
                        <div className="card-body p-3">
                            <h6 className="text-black font-bold mb-2" style={{ fontSize: '15px' }}>UNIDADES VENDIDAS {anioActivo}</h6>
                            <h4 className="font-weight-bold text-dark mb-1">{formatNumber(dashboardData.kpis.unidadesActual)}</h4>
                            <label className="text-muted">vs. {formatNumber(dashboardData.kpis.unidadesAnterior)} en {parseInt(anioActivo) - 1}</label>
                        </div>
                    </div>

                    {/* 5. Producto Más Vendido */}
                    <div className="shadow-sm flex-grow-1" style={{ minWidth: '18%', flexBasis: '18%', border: '2px solid #01a59c', borderRadius: '1.25rem' }}>
                        <div className="card-body p-3">
                            <h6 className="text-black font-bold mb-2" style={{ fontSize: '15px' }}>PRODUCTO MÁS VENDIDO {anioActivo}</h6>
                            <h5 className="font-weight-bold text-dark mb-1 text-truncate" title={dashboardData.kpis.productoEstrella.nombre}>
                                {dashboardData.kpis.productoEstrella.nombre}
                            </h5>
                            <label className="text-muted mb-0">{formatMoney(dashboardData.kpis.productoEstrella.monto)} | {formatNumber(dashboardData.kpis.productoEstrella.unidades)} unid.</label>
                        </div>
                    </div>
                </div>
            )}

            <div className="shadow-sm p-3 mb-4" style={{ border: '2px solid #01a59c', borderRadius: '1.25rem' }}>
                {/* Filtros en Píldoras (Filas Horizontales Apiladas) */}
                <div className="pb-2">
                    {renderFilterGroup('SUCURSAL', sucursalOptions, sucursalActiva, handleSucursalChange)}
                    {renderFilterGroup('SECTOR', sectorOptions, sectorActivo, handleSectorChange)}
                    {renderFilterGroup('VER POR', verPorOptions, verPorActivo, handleVerPorChange)}
                    {renderFilterGroup('MÉTRICA', metricaOptions, metricaActiva, handleMetricaChange)}
                    {renderDynamicSubFilter()}
                </div>

                {/* Fila de Filtro de Productos */}
                <div className="">
                    <div className="d-flex align-items-center flex-wrap p-3 rounded border-0">
                        <span className="font-weight-bold text-secondary mr-3 " style={{ minWidth: '120px' }}><i className="fas fa-search mr-1"></i> Buscar Producto:</span>
                        <div style={{ flex: 1, zIndex: 100, minWidth: '250px' }}>
                            <Select
                                isMulti
                                name="productos"
                                options={productosDisponibles}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                placeholder="Seleccionar uno o varios productos..."
                                value={localSelectedProducts}
                                onChange={setLocalSelectedProducts}
                                noOptionsMessage={() => "No se encontraron productos"}
                                isLoading={loading && productosDisponibles.length === 0}
                            />
                        </div>
                        <button
                            className="btn btn-verde-mfp ml-3 px-4 font-weight-bold shadow-sm"
                            onClick={handleApplyProducts}
                            disabled={loading}
                            style={{ fontSize: '14px' }}
                        >
                            {loading ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-filter mr-1"></i>} Aplicar
                        </button>
                    </div>
                </div>

                {loading && !dashboardData && (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                            <span className="sr-only">Cargando...</span>
                        </div>
                        <p className="mt-3 text-muted font-weight-bold">Sincronizando información...</p>
                    </div>
                )}

                {dashboardData && dashboardData.kpis && (
                    <>

                        {/* Comparativo de Productos Seleccionados */}
                        {localSelectedProducts && localSelectedProducts.length >= 1 && dashboardData.tablaComparativa && (
                            <div className="mt-1 mb-1">
                                <div className="d-flex align-items-center mb-3">
                                    <hr className="flex-grow-1" />
                                    <h6 className="mx-3 text-muted font-weight-bold m-0 text-uppercase">Comparativo de Productos Seleccionados</h6>
                                    <hr className="flex-grow-1" />
                                </div>

                                <div className="d-flex overflow-auto" style={{ gap: '15px' }}>
                                    {/* Tarjeta Total Combinado */}
                                    {(() => {
                                        const seleccionadosIds = localSelectedProducts.map(p => p.value.toString());
                                        const dataProds = dashboardData.tablaComparativa.filter(item => seleccionadosIds.includes(item.producto_id.toString()));

                                        const sumUnidades2025 = dataProds.reduce((sum, item) => sum + (Number(item.unidades_ant) || 0), 0);
                                        const sumUnidades2026 = dataProds.reduce((sum, item) => sum + (Number(item.unidades_act) || 0), 0);
                                        const sumMonto2025 = dataProds.reduce((sum, item) => sum + (Number(item.monto_ant) || 0), 0);
                                        const sumMonto2026 = dataProds.reduce((sum, item) => sum + (Number(item.monto_act) || 0), 0);
                                        const crecimientoTotal = sumMonto2025 > 0 ? ((sumMonto2026 - sumMonto2025) / sumMonto2025 * 100) : 0;

                                        return (
                                            <div className="card shadow-sm border-0 bg-dark text-white" style={{ minWidth: '300px' }}>
                                                <div className="card-body p-3">
                                                    <h6 className="font-weight-bold mb-3">Total combinado ({localSelectedProducts.length} productos)</h6>
                                                    <div className="d-flex justify-content-between mb-1"><small className='p-2'>Unidades {parseInt(anioActivo) - 1}:</small> <span className='p-1'>{formatNumber(sumUnidades2025)}</span></div>
                                                    <div className="d-flex justify-content-between mb-1"><small className='p-2'>Unidades {anioActivo}:</small> <span className='p-1'>{formatNumber(sumUnidades2026)}</span></div>
                                                    <div className="d-flex justify-content-between mb-1"><small className='p-2'>Monto {parseInt(anioActivo) - 1}:</small> <span className='p-1'>{formatMoney(sumMonto2025)}</span></div>
                                                    <div className="d-flex justify-content-between mb-1"><small className='p-2'>Monto {anioActivo}:</small> <span className='p-1'>{formatMoney(sumMonto2026)}</span></div>
                                                    <hr className="border-secondary my-2" />
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <small>Variación:</small>
                                                        <span className={`badge ${crecimientoTotal >= 0 ? 'badge-success' : 'badge-danger'}`}>
                                                            {crecimientoTotal >= 0 ? '▲' : '▼'} {Math.abs(crecimientoTotal).toFixed(2)}%
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Tarjetas Individuales */}
                                    {localSelectedProducts.map(prod => {
                                        const productRows = dashboardData.tablaComparativa.filter(i => i.producto_id.toString() === prod.value.toString());
                                        const unidades2025 = productRows.reduce((sum, item) => sum + (Number(item.unidades_ant) || 0), 0);
                                        const unidades2026 = productRows.reduce((sum, item) => sum + (Number(item.unidades_act) || 0), 0);
                                        const monto2025 = productRows.reduce((sum, item) => sum + (Number(item.monto_ant) || 0), 0);
                                        const monto2026 = productRows.reduce((sum, item) => sum + (Number(item.monto_act) || 0), 0);
                                        const crecimiento = monto2025 > 0 ? ((monto2026 - monto2025) / monto2025 * 100) : 0;

                                        return (
                                            <div key={prod.value} className="card shadow-sm bg-white" style={{ minWidth: '280px', border: '1px solid #dee2e6' }}>
                                                <div className="card-body p-3">
                                                    <h6 className="font-weight-bold mb-3 text-dark text-truncate" title={prod.label}>{prod.label}</h6>
                                                    <div className="d-flex justify-content-between mb-1 text-secondary"><small className='p-2'>Unidades {parseInt(anioActivo) - 1}:</small> <span className='p-1'>{formatNumber(unidades2025)}</span></div>
                                                    <div className="d-flex justify-content-between mb-1 text-secondary"><small className='p-2'>Unidades {anioActivo}:</small> <span className='p-1'>{formatNumber(unidades2026)}</span></div>
                                                    <div className="d-flex justify-content-between mb-1 text-secondary"><small className='p-2'>Monto {parseInt(anioActivo) - 1}:</small> <span className='p-1'>{formatMoney(monto2025)}</span></div>
                                                    <div className="d-flex justify-content-between mb-1 text-secondary"><small className='p-2'>Monto {anioActivo}:</small> <span className='p-1'>{formatMoney(monto2026)}</span></div>
                                                    <hr className="my-2" />
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <small className="text-secondary">Variación:</small>
                                                        <span className={`badge ${crecimiento >= 0 ? 'badge-success' : 'badge-danger'}`}>
                                                            {crecimiento >= 0 ? '▲' : '▼'} {Math.abs(crecimiento).toFixed(2)}%
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {dashboardData && dashboardData.kpis && (
                <>
                    {/* Sección: Metas de Venta (Componente independiente) */}
                    <div className="mb-4 position-relative">
                        <SeccionMetasVenta sucursalActiva={sucursalActiva} sectorActivo={sectorActivo} infoFiltros={infoFiltros} />
                    </div>

                    {/* Sección: Gráficos de Tendencia (Componente independiente) */}
                    <div className="mb-4 position-relative">
                        {loading && (
                            <div className="position-absolute d-flex justify-content-center align-items-center" style={{ top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10 }}>
                                <div className="spinner-border text-primary"></div>
                            </div>
                        )}
                        <SeccionTendencias
                            comparativoMensual={dashboardData.comparativoMensual}
                            topProductos={top10Productos}
                            anioActivo={anioActivo}
                            metricaActiva={metricaActiva}
                            infoFiltros={infoFiltros}
                        />
                    </div>

                    {/* Sección: Detalle por periodo (Componente independiente) */}
                    <div className="mb-4 position-relative">
                        <SeccionDetallePeriodo
                            comparativoMensual={dashboardData.comparativoMensual}
                            anioActivo={anioActivo}
                            loading={loading}
                            metricaActiva={metricaActiva}
                            infoFiltros={infoFiltros}
                        />
                    </div>
                    <div className='mb-4 position-relative'>
                        <SeccionComparativoDetallado
                            data={dashboardData.tablaComparativa}
                            anioActivo={anioActivo}
                            mesActivo={mesActivo}
                            loading={loading}
                            infoFiltros={infoFiltros}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default VentasDashboardUI;
